import { message } from "antd"
import { ScriptGenerationService } from "services/ScriptGenerationService"
import {
  SlideElement,
  STATISTIC_TYPE,
  ELEMENT_TYPE,
  TABLE_OPERATION_TYPE,
  DataElementConfig,
  ChartElementSettings,
  PieChartElementSettings,
  TimeSeriesChartSettings,
  TableElementSettings,
} from "types/ReportingDashboard/elements.types"
import {
  ReportingDashboardConfig,
  TableDataCache,
  PrepareTablesResult,
  ChartElementWithConfig,
  ElementToProcess,
} from "types/ReportingDashboard/reporting.types"
import { QueryInstance } from "types/engine.types"
import {
  EngineQueryResult,
  RTableContent,
  EngineOutputItem,
} from "types/engineOutput.types"
import { AxiosResponse } from "axios"
import { batchGenerateImages } from "services/ChartImageService"

/**
 * Parameters required for preparing dashboard elements for export.
 * @interface PrepareElementsForExportParams
 */
interface PrepareElementsForExportParams {
  /** The complete dashboard configuration containing slides, templates, and settings */
  config: ReportingDashboardConfig
  /** The active query instance for executing engine commands */
  queryInstance: QueryInstance
  /** Runtime cache of table data to avoid redundant queries */
  tableDataCache: TableDataCache
  /** Whether to force refresh all elements regardless of cache status */
  forceRefreshAll?: boolean
  /** Function to execute engine queries and return results */
  RunEngineQuery: (
    instanceId: QueryInstance,
    script: string,
    log: boolean
  ) => Promise<AxiosResponse<unknown>>
  /** Callback to update the table data cache with new data */
  updateTableDataCache: (
    elementId: string,
    tableData: RTableContent | null
  ) => void
  /** Callback to update element configurations with new data (e.g., chart images) */
  handleTableConfigUpdate: (
    elementId: string,
    config: DataElementConfig
  ) => void
}

/**
 * Determines whether a dashboard element should be processed for export.
 *
 * Validates that the element has the required configuration based on its type:
 * - TABLE: Requires at least 2 cross-tab variables
 * - CHART: Requires both selectedVariable and breakdownVariable
 * - PIE_CHART: Requires selectedVariable
 * - TIME_SERIES_CHART: Requires primaryVariable, breakdownVariable, and waveVariable
 *
 * @param {SlideElement} element - The dashboard element to validate
 * @param {DataElementConfig | undefined} config - The element's configuration
 * @returns {boolean} True if the element should be processed, false otherwise
 *
 * @example
 * ```typescript
 * const tableElement = { id: "tbl1", type: ELEMENT_TYPE.TABLE };
 * const tableConfig = { crossTabVariables: ["var1", "var2"] };
 * const shouldProcess = shouldProcessElement(tableElement, tableConfig); // true
 * ```
 */
const shouldProcessElement = (
  element: SlideElement,
  config: DataElementConfig | undefined
): boolean => {
  if (!element?.id || !config) return false

  switch (element.type) {
    case ELEMENT_TYPE.TABLE: {
      const tableConfig = config as TableElementSettings
      return tableConfig.crossTabVariables?.length >= 2
    }
    case ELEMENT_TYPE.CHART: {
      const chartConfig = config as ChartElementSettings
      return !!(chartConfig.selectedVariable && chartConfig.breakdownVariable)
    }
    case ELEMENT_TYPE.PIE_CHART: {
      const pieConfig = config as PieChartElementSettings
      return !!pieConfig.selectedVariable
    }
    case ELEMENT_TYPE.TIME_SERIES_CHART: {
      const timeConfig = config as TimeSeriesChartSettings
      return !!(
        timeConfig.selectedPrimaryVariable &&
        timeConfig.selectedBreakdownVariable &&
        timeConfig.selectedWaveVariable
      )
    }
    default:
      return false
  }
}

/**
 * Collects all processable elements from dashboard slides and templates.
 *
 * Iterates through all slides and their associated templates to find elements
 * that need data processing. Prevents duplicate processing by tracking unique
 * element IDs and respects cache settings.
 *
 * @param {ReportingDashboardConfig} config - Dashboard configuration containing slides and templates
 * @param {TableDataCache} tableDataCache - Current cache of table data
 * @param {boolean} forceRefreshAll - Whether to ignore cache and process all elements
 * @returns {Object} Object containing elements to process and chart elements with configs
 * @returns {ElementToProcess[]} returns.elementsToProcess - All elements needing data processing
 * @returns {ChartElementWithConfig[]} returns.chartElementsWithConfigs - Chart elements needing image generation
 *
 * @example
 * ```typescript
 * const { elementsToProcess, chartElementsWithConfigs } = collectElementsToProcess(
 *   dashboardConfig,
 *   tableCache,
 *   false
 * );
 * console.log(`Found ${elementsToProcess.length} elements to process`);
 * ```
 */
const collectElementsToProcess = (
  config: ReportingDashboardConfig,
  tableDataCache: TableDataCache,
  forceRefreshAll: boolean
): {
  elementsToProcess: ElementToProcess[]
  chartElementsWithConfigs: ChartElementWithConfig[]
} => {
  const currentDashboardConfig = config.dashboard
  const allSlides = currentDashboardConfig.slides || []
  const allTemplates = currentDashboardConfig.templates || {}
  const elementsToProcess: ElementToProcess[] = []
  const chartElementsWithConfigs: ChartElementWithConfig[] = []
  const uniqueElementIdsToProcess = new Set<string>()

  /**
   * Processes a collection of elements (from slides or templates) to identify
   * those needing data processing and chart image generation.
   *
   * @param {SlideElement[]} elements - Array of elements to process
   * @param {Record<string, DataElementConfig>} configs - Element configurations mapping
   */
  const processElements = (
    elements: SlideElement[],
    configs: Record<string, DataElementConfig>
  ) => {
    elements
      .filter((el) =>
        [
          ELEMENT_TYPE.TABLE,
          ELEMENT_TYPE.CHART,
          ELEMENT_TYPE.PIE_CHART,
          ELEMENT_TYPE.TIME_SERIES_CHART,
        ].includes(el.type)
      )
      .forEach((element) => {
        const elementConfig = configs[element.id]
        if (
          element.id &&
          shouldProcessElement(element, elementConfig) &&
          (forceRefreshAll || !tableDataCache[element.id]) &&
          !uniqueElementIdsToProcess.has(element.id)
        ) {
          elementsToProcess.push({
            elementId: element.id,
            elementType: element.type,
            config: elementConfig!,
            element: element,
          })

          if (
            [
              ELEMENT_TYPE.CHART,
              ELEMENT_TYPE.PIE_CHART,
              ELEMENT_TYPE.TIME_SERIES_CHART,
            ].includes(element.type)
          ) {
            const baseConfig = elementConfig as
              | ChartElementSettings
              | PieChartElementSettings
              | TimeSeriesChartSettings

            const configWithDimensions = {
              ...baseConfig,
              width: element.width || baseConfig.width || 600,
              height: element.height || baseConfig.height || 400,
            }

            chartElementsWithConfigs.push({
              elementId: element.id,
              elementType: element.type as
                | ELEMENT_TYPE.CHART
                | ELEMENT_TYPE.PIE_CHART
                | ELEMENT_TYPE.TIME_SERIES_CHART,
              config: configWithDimensions,
              element: element,
            })
          }

          uniqueElementIdsToProcess.add(element.id)
        }
      })
  }

  // Collect all data elements from all slides and templates
  allSlides.forEach((slide) => {
    // Elements from template
    if (slide.templateId && allTemplates[slide.templateId]) {
      const template = allTemplates[slide.templateId]
      const templateElements = template.elements || []
      const templateConfigs = template.tableConfigs || {}
      processElements(templateElements, templateConfigs)
    }

    // Slide-specific elements
    const slideElements = slide.slideElements || []
    const slideConfigs = slide.slideTableConfigs || {}
    processElements(slideElements, slideConfigs)
  })

  return { elementsToProcess, chartElementsWithConfigs }
}

/**
 * Generates the appropriate engine script for a dashboard element based on its type and configuration.
 *
 * Creates engine-specific scripts for different element types:
 * - TABLE: Generates cross-tabulation scripts with operators and optional transposition
 * - CHART: Creates scripts for bar/line charts with breakdown variables
 * - PIE_CHART: Generates scripts for pie chart data (typically using same variable for both axes)
 * - TIME_SERIES_CHART: Creates time-based analysis scripts with wave variables
 *
 * @param {string} elementId - Unique identifier for the element
 * @param {ELEMENT_TYPE} elementType - Type of the dashboard element
 * @param {DataElementConfig} config - Element configuration containing variables and settings
 * @param {string} filter - Global filter to apply to the query (optional)
 * @returns {string} The generated engine script, or empty string if element type not supported
 *
 * @example
 * ```typescript
 * const script = generateScriptForElement(
 *   "chart1",
 *   ELEMENT_TYPE.CHART,
 *   { selectedVariable: "age", breakdownVariable: "gender" },
 *   "region = 'US'"
 * );
 * // Returns: "XTAB tbl_chart1_12345 <<age>> <<gender>> UNIVERSE: <<region = 'US'>> OPERATORS: "N"\nOUTPUT tbl_chart1_12345"
 * ```
 */
const generateScriptForElement = (
  elementId: string,
  elementType: ELEMENT_TYPE,
  config: DataElementConfig,
  filter: string
): string => {
  const uniqueTableIdForEngine = `tbl_${elementId.replace(
    /[^a-zA-Z0-9_]/g,
    "_"
  )}_${Date.now()}`

  if (elementType === ELEMENT_TYPE.TABLE) {
    const tableConfig = config as TableElementSettings
    const var1 = tableConfig.crossTabVariables[0] || ""
    const var2 = tableConfig.crossTabVariables[1] || ""
    return ScriptGenerationService.generateTableScript(
      uniqueTableIdForEngine,
      [var1, var2],
      tableConfig.selectedOperators || [],
      tableConfig.tableOperationType || TABLE_OPERATION_TYPE.XTAB,
      filter,
      tableConfig.isTransposed
    )
  } else if (elementType === ELEMENT_TYPE.CHART) {
    const chartConfig = config as ChartElementSettings
    return ScriptGenerationService.generateChartScript(
      uniqueTableIdForEngine,
      chartConfig.selectedVariable || "",
      chartConfig.breakdownVariable || "",
      chartConfig.selectedStatisticType || STATISTIC_TYPE.N,
      chartConfig.tableOperationType || TABLE_OPERATION_TYPE.XTAB,
      filter
    )
  } else if (elementType === ELEMENT_TYPE.PIE_CHART) {
    const pieConfig = config as PieChartElementSettings
    return ScriptGenerationService.generatePieChartScript(
      uniqueTableIdForEngine,
      pieConfig.selectedVariable || "",
      [pieConfig.selectedVariable || ""],
      pieConfig.selectedStatisticType || STATISTIC_TYPE.N,
      filter
    )
  } else if (elementType === ELEMENT_TYPE.TIME_SERIES_CHART) {
    const timeConfig = config as TimeSeriesChartSettings
    return ScriptGenerationService.generateTimeSeriesScript(
      uniqueTableIdForEngine,
      timeConfig.selectedPrimaryVariable || "",
      timeConfig.selectedBreakdownVariable || "",
      timeConfig.selectedStatisticType || STATISTIC_TYPE.N,
      timeConfig.tableOperationType || TABLE_OPERATION_TYPE.XTAB,
      `${timeConfig.selectedWaveVariable}${filter ? ` AND ${filter}` : ""}`
    )
  }

  return ""
}

/**
 * Processes data elements by executing engine queries and caching results.
 *
 * For each element that needs data processing:
 * 1. Generates the appropriate engine script
 * 2. Executes the script via the engine
 * 3. Processes the response and extracts table data
 * 4. Updates the table data cache
 * 5. Provides user feedback via loading messages
 *
 * @param {ElementToProcess[]} dataElements - Array of elements requiring data processing
 * @param {PrepareElementsForExportParams} params - All required parameters and callbacks
 * @returns {Promise<Record<string, EngineOutputItem>>} Promise resolving to a record of newly fetched data
 *
 * @example
 * ```typescript
 * const newData = await processDataElements(elementsToProcess, {
 *   config: dashboardConfig,
 *   queryInstance,
 *   RunEngineQuery,
 *   updateTableDataCache,
 *   // ... other params
 * });
 * console.log(`Processed ${Object.keys(newData).length} elements`);
 * ```
 */
const processDataElements = async (
  dataElements: ElementToProcess[],
  params: PrepareElementsForExportParams
): Promise<Record<string, EngineOutputItem>> => {
  const { config, queryInstance, RunEngineQuery, updateTableDataCache } = params
  const newlyFetchedData: Record<string, EngineOutputItem> = {}

  if (dataElements.length === 0) return newlyFetchedData

  const itemProcessingMessageKey = "dataExportPrepItem"
  // keyed message flow — notify.toast has no replace-in-place; keep antd message here
  message.loading({
    content: `Preparing ${dataElements.length} data element(s) for export...`,
    key: itemProcessingMessageKey,
    duration: 0,
  })

  for (let i = 0; i < dataElements.length; i++) {
    const { elementId, elementType, config: elementConfig } = dataElements[i]

    if (dataElements.length > 1) {
      message.loading({
        content: `Preparing data element ${i + 1} of ${
          dataElements.length
        } for export...`,
        key: itemProcessingMessageKey,
        duration: 0,
      })
    }

    try {
      const script = generateScriptForElement(
        elementId,
        elementType,
        elementConfig,
        config.filter
      )

      if (!script) {
        console.warn(
          `No script generated for element ${elementId} of type ${elementType}`
        )
        continue
      }

      const result = await RunEngineQuery(queryInstance, script, false)
      const engineResult = result.data as EngineQueryResult

      if (
        result?.status === 200 &&
        engineResult?.result?.output?.[0]?.content
      ) {
        const dataFromResult = engineResult.result.output[0]
          .content as RTableContent

        updateTableDataCache(elementId, dataFromResult)
        newlyFetchedData[elementId] = {
          label: elementId,
          type: "rtable",
          format: "table: array of rows",
          content: dataFromResult,
        }
      } else {
        const engineError =
          engineResult?.result?.output?.[0]?.message ||
          engineResult?.result?.log?.join(" ") ||
          "Engine execution failed"
        console.error(
          `❌ Error processing element ${elementId} for export: ${engineError}`,
          result
        )
      }
    } catch (error) {
      console.error(
        `❌ Exception while processing element ${elementId} for export:`,
        error
      )
    }
  }

  message.destroy(itemProcessingMessageKey)
  return newlyFetchedData
}

/**
 * Generates static images for chart elements using the chart image service.
 *
 * Takes chart elements and:
 * 1. Calls the batch image generation service
 * 2. Updates element configurations with the generated image data URIs
 * 3. Provides user feedback during the generation process
 * 4. Handles errors gracefully with appropriate error messages
 *
 * @param {ChartElementWithConfig[]} chartElementsWithConfigs - Chart elements needing image generation
 * @param {PrepareElementsForExportParams} params - Parameters including query instance and update callbacks
 * @returns {Promise<Record<string, string>>} Promise resolving to a record mapping element IDs to image data URIs
 *
 * @example
 * ```typescript
 * const chartImages = await generateChartImages(chartElements, {
 *   config: dashboardConfig,
 *   queryInstance,
 *   RunEngineQuery,
 *   handleTableConfigUpdate,
 *   // ... other params
 * });
 * console.log(`Generated ${Object.keys(chartImages).length} chart images`);
 * ```
 */
const generateChartImages = async (
  chartElementsWithConfigs: ChartElementWithConfig[],
  params: PrepareElementsForExportParams
): Promise<Record<string, string>> => {
  const { config, queryInstance, RunEngineQuery, handleTableConfigUpdate } =
    params
  let generatedChartImages: Record<string, string> = {}

  if (chartElementsWithConfigs.length === 0) return generatedChartImages

  // keyed message flow — notify.toast has no replace-in-place; keep antd message here
  message.loading({
    content: `Generating images for ${chartElementsWithConfigs.length} chart(s)...`,
    key: "chartImageGeneration",
    duration: 0,
  })

  try {
    generatedChartImages = await batchGenerateImages(
      chartElementsWithConfigs,
      RunEngineQuery,
      queryInstance,
      config.filter
    )

    Object.entries(generatedChartImages).forEach(
      ([elementId, imageDataUri]) => {
        const elementConfig = chartElementsWithConfigs.find(
          (el) => el.elementId === elementId
        )
        if (elementConfig) {
          const updatedConfig: DataElementConfig = {
            ...elementConfig.config,
            chartDataUri: imageDataUri,
          }
          handleTableConfigUpdate(elementId, updatedConfig)
        }
      }
    )

    message.success({
      content: `Generated images for ${
        Object.keys(generatedChartImages).length
      } chart(s)`,
      key: "chartImageGeneration",
      duration: 2,
    })
  } catch (error) {
    console.error("❌ Error generating chart images:", error)
    message.error({
      content: "Failed to generate some chart images",
      key: "chartImageGeneration",
      duration: 3,
    })
  }

  return generatedChartImages
}

/**
 * Prepares dashboard elements for export by processing data and generating chart images.
 *
 * This is the main entry point for preparing dashboard elements for export operations.
 * It orchestrates the entire process of:
 * 1. Collecting all elements that need processing from slides and templates
 * 2. Filtering based on cache status and force refresh settings
 * 3. Processing data elements by executing engine queries
 * 4. Generating static images for chart elements
 * 5. Updating caches and configurations with new data
 *
 * The function is optimized to avoid redundant processing by respecting the table data cache
 * unless explicitly forced to refresh all elements.
 *
 * @param {PrepareElementsForExportParams} params - Complete parameter object containing:
 *   - config: Dashboard configuration with slides and templates
 *   - queryInstance: Active query instance for engine operations
 *   - tableDataCache: Current cache to avoid redundant queries
 *   - forceRefreshAll: Whether to ignore cache and process all elements
 *   - RunEngineQuery: Function to execute engine scripts
 *   - updateTableDataCache: Callback to update the data cache
 *   - handleTableConfigUpdate: Callback to update element configurations
 *
 * @returns {Promise<PrepareTablesResult>} Promise resolving to export preparation results containing:
 *   - newlyFetchedData: Record of element IDs to their freshly fetched engine output
 *   - generatedChartImages: Record of element IDs to their generated image data URIs
 *
 * @throws {Error} Logs warnings for missing query instance but doesn't throw
 *
 * @example
 * ```typescript
 * // Prepare all elements for export
 * const result = await prepareElementsForExport({
 *   config: dashboardConfig,
 *   queryInstance: activeInstance,
 *   tableDataCache: currentCache,
 *   forceRefreshAll: false,
 *   RunEngineQuery: engineQueryFunction,
 *   updateTableDataCache: cacheUpdateCallback,
 *   handleTableConfigUpdate: configUpdateCallback
 * });
 *
 * console.log(`Processed ${Object.keys(result.newlyFetchedData).length} data elements`);
 * console.log(`Generated ${Object.keys(result.generatedChartImages).length} chart images`);
 * ```
 *
 * @example
 * ```typescript
 * // Force refresh all elements (ignore cache)
 * const result = await prepareElementsForExport({
 *   ...params,
 *   forceRefreshAll: true
 * });
 * ```
 *
 * @since 1.0.0
 */
export const prepareElementsForExport = async (
  params: PrepareElementsForExportParams
): Promise<PrepareTablesResult> => {
  const {
    config,
    queryInstance,
    tableDataCache,
    forceRefreshAll = false,
  } = params

  if (!queryInstance) {
    console.warn(
      "prepareElementsForExport: Missing queryInstance. Cannot prepare elements."
    )
    return { newlyFetchedData: {}, generatedChartImages: {} }
  }

  const { elementsToProcess, chartElementsWithConfigs } =
    collectElementsToProcess(config, tableDataCache, forceRefreshAll)

  if (elementsToProcess.length === 0) {
    return { newlyFetchedData: {}, generatedChartImages: {} }
  }

  // Process ALL data elements (tables AND charts) for data fetching
  const dataElements = elementsToProcess.filter(
    (el) =>
      el.elementType === ELEMENT_TYPE.TABLE ||
      el.elementType === ELEMENT_TYPE.CHART ||
      el.elementType === ELEMENT_TYPE.PIE_CHART ||
      el.elementType === ELEMENT_TYPE.TIME_SERIES_CHART
  )

  const newlyFetchedData = await processDataElements(dataElements, params)
  const generatedChartImages = await generateChartImages(
    chartElementsWithConfigs,
    params
  )

  return {
    newlyFetchedData,
    generatedChartImages,
  }
}
